
import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { AuthContext } from './AuthContext';
import { getFCMToken, onForegroundMessage, isPushNotificationSupported } from '../firebase/config';
import * as notificationService from '../services/notificationService';
import { OrderContext } from './OrderContext';


export const NotificationContext = createContext();

const FCM_TOKEN_KEY = 'fcmToken';

export const NotificationProvider = ({ children }) => {
  const { authToken } = useContext(AuthContext);
  const {fetchNewOrders} = useContext(OrderContext);
  const toastRef = useRef(null);
  const fcmInitStartedRef = useRef(false);
  const notificationAudioRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  

  const [pagination, setPagination] = useState({
    pageNumber: 0,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  

  const [fcmInitialized, setFcmInitialized] = useState(false);

  // Latest POS order create/update event received via FCM (see PosOrdersService
  // on the backend - tagged with data.source === 'POS' since POS orders reuse
  // the existing ORDER_PLACED/ORDER_STATUS_CHANGED types instead of new enum
  // values). Consumed by PosDashboard to auto-refresh when it changes.
  const [posOrderUpdate, setPosOrderUpdate] = useState(null);

 
  const initializeFCM = useCallback(async () => {
    try {
      if (!isPushNotificationSupported()) {
        return;
      }
      // Avoid hammering Chrome's PushManager / FCM (can trigger
      // "Registration failed - push service error" / TOO_MANY_REGISTRATIONS).
      if (fcmInitStartedRef.current || fcmInitialized) {
        return;
      }
      fcmInitStartedRef.current = true;

      const token = await getFCMToken();
      
      if (!token) {
        // Allow a later retry (e.g. after user grants permission / clears SW).
        fcmInitStartedRef.current = false;
        return;
      }

      // Always register with the backend on init. Skipping when localStorage
      // already has the same token left stale/inactive rows in device_tokens
      // (e.g. after FCM marked a token inactive) and blocked multi-device /
      // re-login reactivation. Backend register is idempotent and reactivates
      // an existing (partner_id, fcm_token) row.
      await notificationService.registerDevice(token, 'web');
      localStorage.setItem(FCM_TOKEN_KEY, token);

      setFcmInitialized(true);
    } catch (error) {
      console.error('[NotificationContext] FCM initialization error:', error);
      fcmInitStartedRef.current = false;
    }
  }, [fcmInitialized]);

  const unregisterFCM = useCallback(async () => {
    try {
      const token = localStorage.getItem(FCM_TOKEN_KEY);
      
      if (token) {
        // Unregister from backend
        await notificationService.unregisterDevice(token);
        
        // Clear stored token
        localStorage.removeItem(FCM_TOKEN_KEY);
        
      }
      
      setFcmInitialized(false);
    } catch (error) {
      console.error('[NotificationContext] FCM unregistration error:', error);
      // Still clear local token even if API fails
      localStorage.removeItem(FCM_TOKEN_KEY);
    }
  }, []);


  const fetchNotifications = useCallback(async (params = {}) => {
    setLoading(true);
    
    try {
      const response = await notificationService.getNotifications({
        pageNumber: params.pageNumber ?? pagination.pageNumber,
        pageSize: params.pageSize ?? pagination.pageSize,
        is_read: params.is_read,
        type: params.type,
      });

      // Update notifications (append for load more, replace otherwise)
      if (params.append) {
        setNotifications(prev => [...prev, ...response.data]);
      } else {
        setNotifications(response.data);
      }

      // Update pagination state
      setPagination({
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        total: response.total,
        totalPages: response.totalPages,
      });
    } catch (error) {
      console.error('[NotificationContext] Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageNumber, pagination.pageSize]);


  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.count);
    } catch (error) {
      console.error('[NotificationContext] Failed to fetch unread count:', error);
    }
  }, []);


  const loadMoreNotifications = useCallback(async () => {
    if (pagination.pageNumber < pagination.totalPages - 1) {
      await fetchNotifications({
        pageNumber: pagination.pageNumber + 1,
        append: true,
      });
    }
  }, [fetchNotifications, pagination.pageNumber, pagination.totalPages]);


  const markAsRead = useCallback(async (notificationId) => {
    try {
      // Optimistic update - update local state immediately
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, is_read: true, read_at: new Date().toISOString() }
            : notification
        )
      );
      
      // Decrement unread count
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Call API
      await notificationService.markAsRead(notificationId);
    } catch (error) {
      console.error('[NotificationContext] Failed to mark as read:', error);
      // Revert optimistic update on error
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [fetchNotifications, fetchUnreadCount]);


  const markAllAsRead = useCallback(async () => {
    try {
      // Optimistic update
      setNotifications(prev =>
        prev.map(notification => ({
          ...notification,
          is_read: true,
          read_at: notification.read_at || new Date().toISOString(),
        }))
      );
      setUnreadCount(0);

      // Call API
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error('[NotificationContext] Failed to mark all as read:', error);
      // Revert on error
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [fetchNotifications, fetchUnreadCount]);


  // Browsers block Audio.play() until the page has a user gesture. Prefetch the
  // file and unlock playback on the first click/keydown so FCM toasts can chime.
  useEffect(() => {
    try {
      const audio = new Audio('/notification-sound.mp3');
      audio.preload = 'auto';
      audio.volume = 0.45;
      notificationAudioRef.current = audio;
    } catch (error) {
      console.error('[NotificationContext] Failed to create notification audio:', error);
    }

    const events = ['pointerdown', 'keydown', 'touchstart'];

    const unlockAudio = () => {
      const audio = notificationAudioRef.current;
      if (!audio || audioUnlockedRef.current) return;

      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audioUnlockedRef.current = true;
          events.forEach((eventName) => window.removeEventListener(eventName, unlockAudio));
        })
        .catch(() => {
          // Still locked — keep listening for the next gesture.
        });
    };

    events.forEach((eventName) =>
      window.addEventListener(eventName, unlockAudio, { passive: true }),
    );

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, unlockAudio));
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      const audio = notificationAudioRef.current;
      if (!audio) return;

      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0.45;

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((error) => {
          // Common before the first click on the page (autoplay policy).
          console.warn('[NotificationContext] Could not play sound:', error.message);
        });
      }
    } catch (error) {
      console.error('[NotificationContext] Error playing notification sound:', error);
    }
  }, []);


  const handleForegroundMessage = useCallback((payload) => {
    // Play notification sound
    playNotificationSound();

    // Extract notification data
    const title = payload.notification?.title || payload.data?.title || 'New Notification';
    const body = payload.notification?.body || payload.data?.body || '';
    const data = payload.data || {};

    // Show toast notification using primereact Toast
    if (toastRef.current) {
      toastRef.current.show({
        severity: 'info',
        summary: title,
        detail: body,
        life: 4000,
        className: 'custom-notification-toast',
        // Store data for potential click handling
        data: data,
      });
    }

    // Increment unread count
    setUnreadCount(prev => prev + 1);

    if (data.source === 'POS') {
      if (data.posEvent === 'CREATED' || data.posEvent === 'UPDATED') {
        setPosOrderUpdate({ ...data, receivedAt: Date.now() });
      }
    } else if (data.type === 'ORDER_PLACED') {
      fetchNewOrders();
    }
  }, [playNotificationSound, fetchNewOrders]);


  // Register device once per login session — do NOT tie this to
  // handleForegroundMessage or we re-call getToken on every callback identity
  // change and wedge Chrome's push service.
  useEffect(() => {
    if (!authToken) return;
    initializeFCM();
    fetchUnreadCount();
  }, [authToken, initializeFCM, fetchUnreadCount]);

  useEffect(() => {
    if (!authToken) return undefined;

    const unsubscribeMessage = onForegroundMessage(handleForegroundMessage);
    return () => {
      if (unsubscribeMessage) {
        unsubscribeMessage();
      }
    };
  }, [authToken, handleForegroundMessage]);

  useEffect(() => {
    if (!authToken) {
      setNotifications([]);
      setUnreadCount(0);
      setPagination({
        pageNumber: 0,
        pageSize: 20,
        total: 0,
        totalPages: 0,
      });
      setFcmInitialized(false);
      setPosOrderUpdate(null);
      fcmInitStartedRef.current = false;
    }
  }, [authToken]);


  const contextValue = {
    // State
    notifications,
    unreadCount,
    loading,
    pagination,
    fcmInitialized,
    posOrderUpdate,
    
    // Functions
    initializeFCM,
    unregisterFCM,
    fetchNotifications,
    fetchUnreadCount,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    
    // Refresh function for external use
    refreshNotifications: () => {
      fetchNotifications({ pageNumber: 0 });
      fetchUnreadCount();
    },
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      <Toast ref={toastRef} position="top-right" className="app-notification-toast" />
      {children}
    </NotificationContext.Provider>
  );
};


export const useNotifications = () => {
  const context = useContext(NotificationContext);
  
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  
  return context;
};
